import React, { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  GraduationCap,
  Plus,
  Eye,
  Edit,
  FileText,
  Calendar,
  Users,
  BarChart3,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getClasses } from "../../services/classes";

const TeacherAssessments: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [selectedClass, setSelectedClass] = useState("");
  const [view, setView] = useState<"assessments" | "grades" | "analytics">(
    "assessments"
  );

  // Fetch classes for the teacher
  const { data: classesData, isLoading: classesLoading } = useQuery({
    queryKey: ["teacher-classes"],
    queryFn: () => getClasses(),
    enabled: !!user?.id,
  });

  if (classesLoading) {
    return <LoadingSpinner />;
  }

  const classes = classesData?.classes || [];

  // Mock assessment data - replace with actual API calls
  const mockAssessments = [
    {
      id: "1",
      title: "Mathematics Quiz 1",
      type: "Quiz",
      classId: classes[0]?._id,
      className: classes[0]?.name,
      date: "2024-01-15",
      totalMarks: 50,
      submissionsCount: 25,
      totalStudents: 30,
      status: "completed",
    },
    {
      id: "2",
      title: "Science Mid-term Exam",
      type: "Exam",
      classId: classes[1]?._id,
      className: classes[1]?.name,
      date: "2024-01-20",
      totalMarks: 100,
      submissionsCount: 18,
      totalStudents: 20,
      status: "ongoing",
    },
    {
      id: "3",
      title: "English Assignment",
      type: "Assignment",
      classId: classes[0]?._id,
      className: classes[0]?.name,
      date: "2024-01-25",
      totalMarks: 75,
      submissionsCount: 0,
      totalStudents: 30,
      status: "scheduled",
    },
  ];

  const filteredAssessments = selectedClass
    ? mockAssessments.filter(
        (assessment) => assessment.classId === selectedClass
      )
    : mockAssessments;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Assessments & Grades
        </h1>
        <p className="text-gray-600 mt-2">
          Create and manage assessments, quizzes, and student grades
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Class Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Classes</option>
                {classes.map((cls: any) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>

            {/* View Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                View
              </label>
              <div className="flex rounded-md border border-gray-300">
                <button
                  onClick={() => setView("assessments")}
                  className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                    view === "assessments"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Assessments
                </button>
                <button
                  onClick={() => setView("grades")}
                  className={`px-3 py-2 text-sm font-medium ${
                    view === "grades"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Grades
                </button>
                <button
                  onClick={() => setView("analytics")}
                  className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                    view === "analytics"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Analytics
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors">
              <Plus className="h-4 w-4 mr-2" />
              Create Assessment
            </button>
          </div>
        </div>
      </div>

      {/* Content based on view */}
      {view === "assessments" && (
        <div className="space-y-6">
          {/* Assessment Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Total Assessments
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredAssessments.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {
                      filteredAssessments.filter(
                        (a) => a.status === "completed"
                      ).length
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Pending Review
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {
                      filteredAssessments.filter((a) => a.status === "ongoing")
                        .length
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Assessments List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Assessments
              </h2>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredAssessments.length > 0 ? (
                filteredAssessments.map((assessment) => (
                  <div key={assessment.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <GraduationCap className="h-5 w-5 text-blue-600" />
                          <h3 className="text-lg font-medium text-gray-900">
                            {assessment.title}
                          </h3>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              assessment.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : assessment.status === "ongoing"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {assessment.status}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center space-x-6 text-sm text-gray-500">
                          <span>Type: {assessment.type}</span>
                          <span>Class: {assessment.className}</span>
                          <span>
                            Date:{" "}
                            {new Date(assessment.date).toLocaleDateString()}
                          </span>
                          <span>Total Marks: {assessment.totalMarks}</span>
                          <span>
                            Submissions: {assessment.submissionsCount}/
                            {assessment.totalStudents}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-gray-600">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600">
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center">
                  <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No assessments found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {selectedClass
                      ? "No assessments found for the selected class."
                      : "Create your first assessment to get started."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {view === "grades" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Grade Management
          </h2>
          <div className="text-center py-12">
            <GraduationCap className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Grade Management
            </h3>
            <p className="mt-2 text-gray-500">
              Grade management features will be available here. Select an
              assessment to view and manage grades.
            </p>
          </div>
        </div>
      )}

      {view === "analytics" && (
        <div className="space-y-6">
          {/* Analytics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <BarChart3 className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Average Score
                  </p>
                  <p className="text-2xl font-bold text-gray-900">84.5%</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Pass Rate</p>
                  <p className="text-2xl font-bold text-gray-900">92%</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <GraduationCap className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Top Performer
                  </p>
                  <p className="text-lg font-bold text-gray-900">98%</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Needs Help
                  </p>
                  <p className="text-2xl font-bold text-gray-900">3</p>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Performance Analytics
            </h2>
            <div className="text-center py-12">
              <BarChart3 className="mx-auto h-16 w-16 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Analytics Dashboard
              </h3>
              <p className="mt-2 text-gray-500">
                Detailed analytics and performance charts will be displayed
                here.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAssessments;
